import { useTranslation } from 'react-i18next';
import { Listbox, Transition } from '@headlessui/react';
import { Fragment, useState } from 'react';
import { languages } from '../i18n';
import Modal from './Modal';

export default function LanguageSelector() {
  const { i18n, t } = useTranslation();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingLanguage, setPendingLanguage] = useState<string | null>(null);
  
  const handleLanguageSelect = (lng: string) => {
    if (lng !== i18n.language) {
      setPendingLanguage(lng);
      setShowConfirmModal(true);
    } else {
      // Si es selecciona el mateix idioma actual, només canviem l'idioma sense confirmació
      i18n.changeLanguage(lng);
    }
  };

  const confirmLanguageChange = () => {
    if (pendingLanguage) {
      // Canviar idioma
      i18n.changeLanguage(pendingLanguage);
      
      // Esborrar els items del carret
      localStorage.removeItem('cartItems');
      
      // Reiniciar la selecció de país i estat
      localStorage.removeItem('selectedCountry');
      localStorage.removeItem('selectedState');
      localStorage.removeItem('tempPostcode');
      localStorage.removeItem('tempCity');
      
      // Disparar events per notificar altres components
      window.dispatchEvent(new Event('storageUpdated'));
      window.dispatchEvent(new Event('resetCountrySelection'));
    }
    
    // Tancar modal
    setShowConfirmModal(false);
    setPendingLanguage(null);
  };

  const cancelLanguageChange = () => {
    setShowConfirmModal(false);
    setPendingLanguage(null);
  };

  const selectedLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  return (
    <>
      <div className="language-selector w-full">
        <Listbox value={selectedLanguage.code} onChange={handleLanguageSelect}>
          <div className="relative w-full">
            <Listbox.Button className="flex w-full items-center justify-between gap-2 pl-2 pr-7 bg-black hover:bg-gray-900 text-white rounded-lg h-9 border border-white relative">
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                </svg>
                <span className="block truncate ml-2">{selectedLanguage.name}</span>
              </div>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </span>
            </Listbox.Button>
            <Transition
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Listbox.Options className="absolute mt-1 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm z-10">
                {languages.map((lang) => (
                  <Listbox.Option
                    key={lang.code}
                    className={({ active }) =>
                      `relative cursor-default select-none py-2 pl-10 pr-4 ${
                        active ? 'bg-blue-100 text-blue-900' : 'text-gray-900'
                      }`
                    }
                    value={lang.code}
                  >
                    {({ selected }) => (
                      <>
                        <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                          {lang.name}
                        </span>
                        {selected ? (
                          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </span>
                        ) : null}
                      </>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>
          </div>
        </Listbox>
      </div>

      {/* Modal de confirmació */}
      <Modal
        isOpen={showConfirmModal}
        onClose={cancelLanguageChange}
        title={t('confirm_language_change')}
        message={t('language_change_warning')}
        type="warning"
        confirmText={t('confirm')}
        showCancel={true}
        cancelText={t('cancel')}
        onConfirm={confirmLanguageChange}
      />
    </>
  );
}